import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import type { OmsConditionStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { ConditionDataSource } from "@redotech/redo-flows-common/steps";
import type { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { useTeamFeatureFlag } from "@redotech/redo-merchant-app-common/use-team-feature-flag";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import type {
  PartialFlowStep,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from ".";
import { getOmsConditionTypeOptions } from "./oms/oms-condition";
import { RecognizeConditionInputs } from "./recognize-condition-inputs";
import { stepTypes } from "./step-types";

type ConditionField =
  | OmsConditionType
  | "recognize-profile"
  | "socialMediaFollowerCount";

interface RecognizeConditionProps extends StepTypeDetailsProps<PartialFlowStep> {
  step: Extract<
    PartialFlowStep,
    { type: StepType.CONDITION | StepType.OMS_CONDITION }
  >;
}

function getRecognizeExpression(step: RecognizeConditionProps["step"]) {
  if (step.type !== StepType.CONDITION) {
    return undefined;
  }

  const expression = step.expression;

  if (expression?.dataSource !== ConditionDataSource.TriggerData) {
    return undefined;
  }

  return expression.schemaBooleanExpression;
}

export const RecognizeCondition = memo(function RecognizeCondition(
  props: RecognizeConditionProps,
) {
  const { step, setStep, onClose } = props;
  const { t } = useTranslation();
  const { enabled: canCreateFollowerCountConditions } = useTeamFeatureFlag(
    OmsFlagKey.RECOGNIZE_FOLLOWER_COUNT_CONDITIONS,
    { forceEnabledForRedoUsers: false },
  );
  const profileOption: TextRedoListItem<ConditionField> = {
    id: "recognize-profile",
    type: "text",
    text: t("{{productName}} profile", { productName: "Recognize" }),
    value: "recognize-profile",
  };
  const followerCountOption: TextRedoListItem<ConditionField> = {
    id: "socialMediaFollowerCount",
    type: "text",
    text: t("Social media follower count"),
    value: "socialMediaFollowerCount",
  };
  const team = useContext(TeamContext);
  const [searchString, setSearchString] = useState("");
  const fieldOptions: TextRedoListItem<ConditionField>[] = [
    ...getOmsConditionTypeOptions(undefined, team),
    profileOption,
  ];
  if (canCreateFollowerCountConditions) {
    fieldOptions.push(followerCountOption);
  }

  const searchText = searchString.toLowerCase();
  const matchingFields = fieldOptions.filter((option) => {
    const label = option.text.toLowerCase();
    return label.includes(searchText);
  });

  const expression = getRecognizeExpression(step);
  const isFollowerCountCondition = expression?.type === "number_comparison";

  let selectedField: TextRedoListItem<ConditionField> | undefined;

  if (step.type === StepType.OMS_CONDITION) {
    selectedField = fieldOptions.find(
      (option) => option.value === step.conditionType,
    );
  } else if (isFollowerCountCondition) {
    selectedField = followerCountOption;
  } else {
    selectedField = profileOption;
  }

  const fieldPicker = (
    <RedoSingleSelectDropdownInput
      fitToAnchor={false}
      flexGrow={false}
      options={matchingFields}
      optionSelected={({ item }) => {
        const connections = {
          id: step.id,
          nextTrueId: step.nextTrueId,
          nextFalseId: step.nextFalseId,
          customTitle: step.customTitle,
        };

        if (item.value === "recognize-profile") {
          setStep({
            ...connections,
            type: StepType.CONDITION,
            expression: { dataSource: ConditionDataSource.TriggerData },
          });
          return;
        }

        if (item.value === "socialMediaFollowerCount") {
          setStep({
            ...connections,
            type: StepType.CONDITION,
            expression: {
              dataSource: ConditionDataSource.TriggerData,
              schemaBooleanExpression: {
                type: "number_comparison",
                operator: "greaterThanOrEqual",
              },
            },
          });
          return;
        }

        const condition: PartialWithRequiredType<OmsConditionStep> = {
          ...connections,
          type: StepType.OMS_CONDITION,
        };
        condition.conditionType = item.value;
        setStep(condition);
      }}
      placeholder={t("Select a field")}
      searchPlaceholder={t("Search conditions...")}
      searchString={searchString}
      selectedItem={selectedField}
      setSearchString={setSearchString}
      size="xs"
    />
  );

  if (step.type === StepType.OMS_CONDITION) {
    const omsCondition = stepTypes[StepType.OMS_CONDITION];
    if (omsCondition.type !== "custom") {
      throw new Error("OMS conditions require a custom inspector");
    }
    return (
      <omsCondition.customInspector {...props} fieldPicker={fieldPicker} />
    );
  }

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="semibold">
          {t("Condition")}
        </Text>
        {onClose && (
          <RedoButton
            aria-label={t("Close condition")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="xs"
          />
        )}
      </Flex>
      <Flex dir="column" gap="none">
        <Text fontSize="xs" fontWeight="medium">
          {t("If")}
        </Text>
        <RecognizeConditionInputs
          expression={expression}
          fieldPicker={fieldPicker}
          setStep={setStep}
          step={step}
        />
      </Flex>
    </Flex>
  );
});
